import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import logo from '../assets/icons/blue logo .jpg';

function Footer() {
  const [showButton, setShowButton] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onScroll = () => setShowButton(window.pageYOffset > 240);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return;
    setLoading(true);

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          tags: ['website', 'footer'],
          source: 'Footer form',
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to subscribe');

      setSubscribed(true);
      setEmail('');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const year = new Date().getFullYear();

  return (
    <footer className="relative text-customNavy border-t border-customNavy">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-x-12 gap-y-10 items-start">
          {/* OUR WORLD */}
          <div className="md:col-span-1">
            <h3 className="text-sm md:text-md tracking-wide uppercase mb-4 !text-customNavy">Our World</h3>
            <ul className="space-y-2 text-xs md:text-sm">
              <li><Link className="hover:underline !text-customNavy" to="/pages/about-us">Our Story</Link></li>
              <li><Link className="hover:underline !text-customNavy" to="/pages/press">Press</Link></li>
              <li><Link className="hover:underline !text-customNavy" to="/pages/stockists">Stockists</Link></li>
            </ul>

            {/* Instagram */}
            <div className="mt-4">
              <a
                href="https://www.instagram.com/thisbelongs.to/"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="This Belongs To on Instagram"
                className="inline-flex items-center p-1 rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-customNavy/40 !text-customNavy"
              >
                <FontAwesomeIcon icon={faInstagram} size="xl" />
              </a>
            </div>
          </div>

          {/* NEED HELP */}
          <div className="md:col-span-1">
            <h3 className="text-sm md:text-md tracking-wide uppercase mb-4 !text-customNavy">Need Help</h3>
            <ul className="space-y-2 text-xs md:text-sm">
              <li><Link className="hover:underline !text-customNavy" to="/pages/contact">Contact us</Link></li>
              <li><Link className="hover:underline !text-customNavy" to="/pages/size-guide">Size Guide</Link></li>
              <li><Link className="hover:underline !text-customNavy" to="/pages/shipping-and-returns">Shippings &amp; Returns</Link></li>
            </ul>
          </div>

          {/* FIND US AT */}
          <div className="md:col-span-1">
            <h3 className="text-sm tracking-wide uppercase mb-4 !text-customNavy">Find Us At</h3>
            <address className="not-italic leading-relaxed text-xs">
              <a
                href="https://www.google.com/maps/search/?api=1&query=Alta+store%2C+34+Great+Windmill+Street%2C+Soho%2C+London"
                target="_blank"
                rel="noreferrer noopener"
                className="hover:underline !text-customNavy"
              >
                Alta Store<br />
                34 Great Windmill Street<br />
                Soho, London
              </a>
            </address>
          </div>

          {/* NEWSLETTER */}
          <div className="md:col-span-2">
            <h3 className="text-sm md:text-md tracking-wide uppercase mb-4 !text-customNavy">Newsletter</h3>

            {!subscribed ? (
              <form onSubmit={handleSubscribe} className="text-customNavy text-xs md:text-sm">
                <p className="mb-3">Subscribe to the world of TBT and get 10% off</p>

                <div className="w-full max-w-xs">
                  <label htmlFor="newsletter-email" className="sr-only">Email</label>
                  <input
                    id="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="email"
                    className="block w-full border border-customNavy bg-transparent text-customNavy placeholder:text-customNavy/60 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-customNavy/40 disabled:opacity-60"
                    disabled={loading}
                  />

                  <button
                    type="submit"
                    aria-label="Submit email"
                    className="block mx-auto mt-3 px-8 py-2 bg-transparent hover:bg-customNavy/5 transition text-sm tracking-wide uppercase text-customNavy disabled:opacity-60"
                    disabled={loading}
                  >
                    {loading ? 'Submitting…' : 'Enter'}
                  </button>

                  {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
                </div>
              </form>
            ) : (
              <div className="max-w-md border border-customNavy bg-customNavy/5 px-4 py-3">
                <p className="mb-1">Thanks for subscribing!</p>
                <p className="text-sm">
                  Use code <span className="font-bold">TBTWORLD</span> for 10% off your next purchase.
                </p>
              </div>
            )}
          </div>

          {/* RIGHT-SIDE CLIENT LOGO */}
          <div className="hidden md:flex md:col-span-1 md:justify-end md:col-start-6">
            <img
              src={logo}
              alt="Client logo"
              className="w-28 h-40 object-contain p-2"
              loading="lazy"
              decoding="async"
              draggable="false"
            />
          </div>
        </div>

        {/* Legal */}
        <div className="mt-10 text-center text-sm text-customNavy/70">
          © {year} This Belongs To — All Rights Reserved
        </div>
      </div>

      {/* Back to top */}
      {showButton && (
        <button
          onClick={scrollToTop}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 rounded-full border border-customNavy bg-white/90 backdrop-blur px-3 py-2 shadow hover:bg-customNavy/5 focus:outline-none focus:ring-2 focus:ring-customNavy/40 !text-customNavy"
        >
          ↑
        </button>
      )}
    </footer>
  );
}

export default Footer;
